import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

export const voucherKindSchema = z.enum(['discount', 'time']);

const voucherBaseSchema = z.object({
  code: z.string()
    .min(3, 'Código deve ter pelo menos 3 caracteres')
    .max(50, 'Código deve ter no máximo 50 caracteres')
    .regex(/^[A-Z0-9_-]+$/, 'Código deve conter apenas letras maiúsculas, números, hífen e underscore'),

  name: z.string()
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres'),

  description: z.string()
    .max(500, 'Descrição deve ter no máximo 500 caracteres')
    .optional(),

  isActive: z.boolean().default(true),

  voucherKind: voucherKindSchema.default('discount'),

  discountType: z.enum(['percentage', 'fixedAmount', 'freeShipping']).optional(),
  discountValue: z.number().min(0, 'Valor do desconto deve ser maior ou igual a 0').optional(),
  freeShipping: z.boolean().optional(),

  durationDays: z.number().int().min(1, 'Duração deve ser de pelo menos 1 dia').optional(),

  maxUses: z.number().min(1, 'Uso máximo deve ser pelo menos 1').optional(),
  maxUsesPerUser: z.number().min(1, 'Uso máximo por usuário deve ser pelo menos 1').optional(),

  validFrom: z.string()
    .min(1, 'Data de início é obrigatória')
    .refine((val) => !isNaN(Date.parse(val)), 'Data de início deve ser uma data válida'),

  validUntil: z.string()
    .min(1, 'Data de fim é obrigatória')
    .refine((val) => !isNaN(Date.parse(val)), 'Data de fim deve ser uma data válida'),

  timezone: z.string().min(1, 'Timezone é obrigatório').default('UTC'),

  packageRestrictions: z.object({
    type: z.enum(['include', 'exclude']),
    packageIds: z.array(z.string()).min(1, 'Selecione pelo menos um pacote'),
  }).optional(),

  categoryRestrictions: z.object({
    type: z.enum(['include', 'exclude']),
    categoryIds: z.array(z.string()).min(1, 'Selecione pelo menos uma categoria'),
  }).optional(),

  productRestrictions: z.object({
    type: z.enum(['include', 'exclude']),
    productIds: z.array(z.string()).min(1, 'Selecione pelo menos um produto'),
  }).optional(),
});

export const voucherBenefitStepSchema = voucherBaseSchema
  .pick({
    voucherKind: true,
    discountType: true,
    discountValue: true,
    freeShipping: true,
    durationDays: true,
  })
  .superRefine((data, ctx) => {
    if (data.voucherKind === 'time') {
      if (!data.durationDays || data.durationDays < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Duração em dias é obrigatória para voucher de tempo',
          path: ['durationDays'],
        });
      }
    }
  });

export const createVoucherSchema = voucherBaseSchema.superRefine((data, ctx) => {
  if (data.validFrom && data.validUntil && new Date(data.validFrom) >= new Date(data.validUntil)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Data de fim deve ser posterior à data de início',
      path: ['validUntil'],
    });
  }

  if (data.voucherKind === 'time') {
    if (!data.durationDays || data.durationDays < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Duração em dias é obrigatória para voucher de tempo',
        path: ['durationDays'],
      });
    }
    return;
  }

  if (!data.discountType) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Tipo de desconto é obrigatório',
      path: ['discountType'],
    });
  }

  if (data.discountType === 'percentage' && (data.discountValue ?? 0) > 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Percentual deve ser menor ou igual a 100%',
      path: ['discountValue'],
    });
  }

  if (data.discountType === 'freeShipping' && !data.freeShipping) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Frete grátis deve estar habilitado para este tipo de desconto',
      path: ['freeShipping'],
    });
  }
});

export const updateVoucherSchema = createVoucherSchema.partial();

export const voucherFiltersSchema = z.object({
  search: z.string().optional(),
  status: z.enum(['Active', 'Inactive', 'Expired']).optional(),
  isActive: z.boolean().optional(),
  discountType: z.enum(['percentage', 'fixedAmount', 'freeShipping']).optional(),
  createdAfter: z.string().optional(),
  createdBefore: z.string().optional(),
  validAfter: z.string().optional(),
  validBefore: z.string().optional(),
  usageRange: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }).optional(),
  createdBy: z.string().optional(),
});

export type CreateVoucherFormData = z.infer<typeof createVoucherSchema>;
export type UpdateVoucherFormData = z.infer<typeof updateVoucherSchema>;
export type VoucherFiltersFormData = z.infer<typeof voucherFiltersSchema>;

export const createVoucherResolver = zodResolver(createVoucherSchema);
export const updateVoucherResolver = zodResolver(updateVoucherSchema);
export const voucherFiltersResolver = zodResolver(voucherFiltersSchema);
